import type { SelectProps } from 'antd';
import { Select, Tooltip, Empty, Tag } from 'antd';
import type { DefaultOptionType } from 'antd/lib/select';
import { createUseStyles } from 'react-jss';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { useMemo } from 'react';
import { searchFilter } from '~/utils/search-filter';
const { Option } = Select;

export interface SearchableListItem {
  value: string;
  displayValue: React.ReactNode;
  searchValues?: (string | undefined | null)[];
  description?: string;
  disabled?: boolean;
}

export interface SearchableInputProps extends SelectProps {
  searchList: SearchableListItem[];
  multiple?: boolean;
  disabled?: boolean;
  // render custom tag for selected items (multiselect), provides expanded data display
  inlineDisplay?: (value: string) => {
    displayValue: string;
  };
  placeholder?: string;
}

const useStyles = createUseStyles({
  select: {
    width: '100%',
  },
  dropdown: {
    '& .ant-select-item-option': {
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  },
});

export const SearchableInput = ({
  searchList,
  onChange,
  multiple,
  inlineDisplay,
  placeholder = 'Search to select',
  ...props
}: SearchableInputProps) => {
  const classes = useStyles();

  const tagRender = useMemo(() => {
    if (!inlineDisplay) return;

    // eslint-disable-next-line react/display-name
    return (tagProps: CustomTagProps) => {
      const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
      };

      const display = inlineDisplay(tagProps.value);

      return (
        <Tag {...tagProps} onMouseDown={onPreventMouseDown}>
          {display.displayValue}
        </Tag>
      );
    };
  }, [inlineDisplay]);

  return (
    <Select
      mode={multiple ? 'multiple' : undefined}
      listHeight={384}
      onChange={onChange}
      showSearch
      className={classes.select}
      popupClassName={classes.dropdown}
      placeholder={placeholder}
      notFoundContent={<Empty description={'No data'} />}
      filterOption={(input: string, option: DefaultOptionType | undefined) => {
        const searchValues = [String(option?.value)];

        if (typeof option?.displayValue === 'string') {
          searchValues.push(String(option.displayValue));
        }

        if (option?.searchValues) {
          searchValues.push(...option.searchValues);
        }

        return searchFilter(input, searchValues);
      }}
      tagRender={tagRender}
      {...props}
    >
      {searchList.map((val) => (
        <Option
          value={val.value}
          key={val.value}
          displayValue={val.displayValue}
          searchValues={val.searchValues}
          disabled={val.disabled}
        >
          <Tooltip title={val.description}>{val.displayValue}</Tooltip>
        </Option>
      ))}
    </Select>
  );
};
